import { FinaleStockField } from "@redotech/congo-enums/finale-stock-field";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const finaleLocation = z.object({
  externalId: z.string(),
  name: z.string(),
  address1: z.string().nullable(),
  city: z.string().nullable(),
  provinceCode: z.string().nullable(),
  /** The merchant's sender-name override; the location name is used when unset. */
  shippingName: z.string().nullable(),
});

/**
 * Credentials are validated against Finale before anything is stored, so a bad
 * key fails loudly instead of being saved and failing later in a cron.
 */
export const connectFinaleIntegration = {
  name: "connectFinaleIntegration" as const,
  input: z.object({
    /** Accepts the account path or a pasted dashboard URL. */
    accountPath: z.string().min(1, "Account path is required"),
    apiKey: z.string().min(1, "API key is required"),
    apiSecret: z.string().min(1, "API secret is required"),
  }),
  output: z.object({
    /**
     * Facilities Finale returned. Mirroring them as locations and sweeping their
     * stock happens on a workflow, so the locations appear shortly after.
     */
    facilityCount: z.number(),
  }),
};

export const getFinaleIntegration = {
  name: "getFinaleIntegration" as const,
  input: z.object({}),
  output: z.object({
    integration: z
      .object({
        enabled: z.boolean(),
        accountPath: z.string(),
        stockField: createEnum(FinaleStockField),
        /** Null until a merchant picks one; the panel shows stockOnHand. */
        onHandField: createEnum(FinaleStockField).nullable(),
        inventoryLastSyncedAt: z.date().nullable(),
        authError: z.boolean(),
        /** Whether the OMS reads and gates on Finale's numbers. */
        isInventorySource: z.boolean(),
        /** Whether a kit counts what its components could build. */
        includeBomStock: z.boolean(),
      })
      .nullable(),
    /** The locations Finale's stock is read from, and shipped from. */
    locations: z.array(finaleLocation),
  }),
};

export const updateFinaleIntegration = {
  name: "updateFinaleIntegration" as const,
  input: z.object({
    enabled: z.boolean().optional(),
    /** What Redo reports as available. */
    stockField: createEnum(FinaleStockField).optional(),
    /** What Redo reports as on hand. The gap to `stockField` is committed. */
    onHandField: createEnum(FinaleStockField).optional(),
    /**
     * Points the OMS at Finale's numbers, or back at Redo's own. Separate from
     * connecting, because it changes what gates label purchases.
     */
    useAsInventorySource: z.boolean().optional(),
    /**
     * Counts a kit's stock from what its components could build. Off, Finale
     * reports a pick-time assembly as zero however much base stock is on hand.
     */
    includeBomStock: z.boolean().optional(),
  }),
  output: z.object({}),
};

export const disconnectFinaleIntegration = {
  name: "disconnectFinaleIntegration" as const,
  input: z.object({}),
  output: z.object({}),
};
